import axios from "axios";
import { getApiUrlWithVersion } from "./environmentService";
import { getAuthUser } from "./authService";

export interface EditionOption {
  id: number;
  name: string;
}

export interface DailyRollupEntry {
  day: string;
  delivered: number;
  opened: number;
  unique_opened: number;
  clicked: number;
  unique_clicked: number;
  unsubscribed: number;
  bounced: number;
}

export interface EditionTotals {
  edition_id: number;
  title: string;
  delivered: number;
  opened: number;
  unique_opened: number;
  clicked: number;
  unique_clicked: number;
  unsubscribed: number;
  bounced: number;
}

export interface HotColdSubscribers {
  total_active: number;
  hot_count: number;
  cold_count: number;
  edition_ids: number[];
}

export interface SubscriberGrowthEntry {
  day: string;
  total_subscribers: number;
  new_subscribers: number;
  lost_subscribers: number;
}

export const getDailyRollup = async (startDate: Date, endDate: Date) => {
  const params = new URLSearchParams();
  params.append("start_date", startDate.toISOString().split("T")[0]);
  params.append("end_date", endDate.toISOString().split("T")[0]);

  const response = await axios.get(
    `${getApiUrlWithVersion()}/analytics/daily_rollup?${params.toString()}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getDailyRollupByEditions = async (
  startDate: Date,
  endDate: Date,
  editionIds: number[],
  step?: string
): Promise<DailyRollupEntry[]> => {
  const params = new URLSearchParams();
  params.append("start_date", startDate.toISOString().split("T")[0]);
  params.append("end_date", endDate.toISOString().split("T")[0]);

  editionIds.forEach((id) => {
    params.append("edition_ids[]", id.toString());
  });

  if (step && step !== "daily") {
    params.append("step", step);
  }

  const response = await axios.get(
    `${getApiUrlWithVersion()}/analytics/daily_rollup_by_editions?${params.toString()}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getEditionsForAnalytics = async (): Promise<EditionOption[]> => {
  const response = await axios.get(
    `${getApiUrlWithVersion()}/analytics/editions`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getEditionTotals = async (
  startDate: Date,
  endDate: Date,
  editionIds: number[]
): Promise<EditionTotals[]> => {
  const params = new URLSearchParams();
  params.append("start_date", startDate.toISOString().split("T")[0]);
  params.append("end_date", endDate.toISOString().split("T")[0]);

  editionIds.forEach((id) => {
    params.append("edition_ids[]", id.toString());
  });

  const response = await axios.get(
    `${getApiUrlWithVersion()}/analytics/edition_totals?${params.toString()}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getHotColdSubscribers = async (
  editionIds: number[]
): Promise<HotColdSubscribers> => {
  const params = new URLSearchParams();

  editionIds.forEach((id) => {
    params.append("edition_ids[]", id.toString());
  });

  const response = await axios.get(
    `${getApiUrlWithVersion()}/analytics/hot_cold_subscribers?${params.toString()}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getSubscriberGrowth = async (
  startDate: Date,
  endDate: Date,
  step?: string
): Promise<SubscriberGrowthEntry[]> => {
  const params = new URLSearchParams();
  params.append("start_date", startDate.toISOString().split("T")[0]);
  params.append("end_date", endDate.toISOString().split("T")[0]);

  if (step && step !== "daily") {
    params.append("step", step);
  }

  const response = await axios.get(
    `${getApiUrlWithVersion()}/analytics/subscriber_growth?${params.toString()}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// --- Per-Edition Analytics ---

export interface LinkClickRow {
  original_link: string;
  total_clicks: number;
  verified_total_clicks: number;
  unique_clicks: number;
  verified_unique_clicks: number;
}

export interface EditionSummary {
  edition: {
    id: number;
    title: string;
    status: string;
    scheduled_send_start_date: string | null;
  };
  overview: {
    delivered: number;
    opened: number;
    unique_opened: number;
    clicked: number;
    unique_clicked: number;
    verified_total_clicks: number;
    verified_unique_clicks: number;
    unsubscribed: number;
    bounced: number;
  };
  link_clicks: LinkClickRow[];
}

export interface EditionSubscriberRow {
  subscriber_id: number;
  email: string;
  engagement_status: string;
  latest_event_at: string;
  total_opens: number;
  total_clicks: number;
  verified_clicks: number;
}

export interface EditionSubscribersResponse {
  subscribers: EditionSubscriberRow[];
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
}

export const getEditionSummary = async (
  editionId: string
): Promise<EditionSummary> => {
  const response = await axios.get(
    `${getApiUrlWithVersion()}/analytics/edition/${editionId}/summary`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getEditionSubscribers = async (
  editionId: string,
  page: number = 1,
  perPage: number = 20,
  search?: string,
  statusFilter?: string,
  link?: string
): Promise<EditionSubscribersResponse> => {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("per_page", perPage.toString());
  if (search) params.append("search", search);
  if (statusFilter) params.append("status", statusFilter);
  if (link) params.append("link", link);

  const response = await axios.get(
    `${getApiUrlWithVersion()}/analytics/edition/${editionId}/subscribers?${params.toString()}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

const getAuthHeaders = () => ({ Authorization: getAuthUser()?.accessToken });
